import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import track, { TrackingPropType } from 'react-tracking'
import { Route, Switch, Redirect } from 'react-router-dom'

import { Button } from '@ynap/button'
import ExitWarning from '@ynap/exit-warning'
import BEMHelper from '@ynap/bem-helper'
import EmptyList from '@ynap/empty-list'
import { MultiStepForm, Step } from '@ynap/multi-step-form'
import { ScrollToTopOnMount as ScrollToTop } from '@ynap/scroll-to-top'

import { myaccountPageData } from '../../analytics'
import { PreviousPageLink } from '../../MyAccountNav'
import ReviewReturn from '../ReviewReturn/ReviewReturn'
import CreateReturn from '../CreateReturn/CreateReturn'
import { OrderTracking } from '../../OrderHistory/OrderTracking'
import CreateCollection from '../CreateCollection/CreateCollection'
import OrderInformation from '../OrderInformation/OrderInformation'
import ReturnConfirmation from '../ReturnConfirmation/ReturnConfirmation'
import getUserSummary from 'app/state/myaccount-state/actions/getUserSummary'
import { getCourierFromTrackingUrl, getCourierFromCollectionService } from './utils/courier'

const bem = new BEMHelper('OrderDetails')

@track({})
export default class OrderDetails extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            isEip: false,
            isVip: false,
        }
    }

    static propTypes = {
        messages: PropTypes.shape({
            orderDetails: PropTypes.shape({
                title: PropTypes.func.isRequired,
                orderInfo: PropTypes.func.isRequired,
                trackOrder: PropTypes.func.isRequired,
                dateShipped: PropTypes.func.isRequired,
                returnInfo: PropTypes.func,
                pageError: PropTypes.func.isRequired,
                retryButton: PropTypes.func.isRequired,
                exit: PropTypes.func.isRequired,
            }),
            price: PropTypes.shape({
                discountPercentage: PropTypes.number,
            }),
        }),
        className: PropTypes.string,
        fetchOrderDetails: PropTypes.func.isRequired,
        externalOrderId: PropTypes.string,
        displayId: PropTypes.string,
        orderType: PropTypes.string,
        showExchangeDerivedOrderIds: PropTypes.bool,
        orderDate: PropTypes.string,
        totals: PropTypes.object,
        orderItems: PropTypes.arrayOf(
            PropTypes.shape({
                partNumber: PropTypes.string,
                image: PropTypes.string,
                brand: PropTypes.string,
                name: PropTypes.string,
                colour: PropTypes.string,
                size: PropTypes.string,
                quantity: PropTypes.string,
                price: PropTypes.object,
            })
        ),
        orderItemTotal: PropTypes.string,
        shippingAddress: PropTypes.object,
        shippingMethod: PropTypes.array,
        paymentMethod: PropTypes.array,
        trackingUrl: PropTypes.string,
        shortDateShipped: PropTypes.string,
        dateShipped: PropTypes.string,
        dateShippedRaw: PropTypes.string,
        isReturnable: PropTypes.string,
        isLegacyOrder: PropTypes.string,
        fetchOrderDetailsError: PropTypes.bool,
        fetchContent: PropTypes.func.isRequired,
        returnSuccess: PropTypes.bool,
        addReturn: PropTypes.func.isRequired,
        clearReturn: PropTypes.func.isRequired,
        clearAllReturns: PropTypes.func.isRequired,
        createReturn: PropTypes.func.isRequired,
        returnOrder: PropTypes.object.isRequired,
        returnOrderError: PropTypes.object,
        locale: PropTypes.object.isRequired,
        selectProductColour: PropTypes.func.isRequired,
        selectReturnCode: PropTypes.func.isRequired,
        selectReturnOrExchange: PropTypes.func.isRequired,
        returnItems: PropTypes.array.isRequired,
        push: PropTypes.func.isRequired,
        email: PropTypes.string,
        name: PropTypes.string,
        userTitle: PropTypes.string,
        userLastName: PropTypes.string,
        location: PropTypes.string,
        helpPage: PropTypes.string,
        timeDelivered: PropTypes.string,
        timeDeliveredRaw: PropTypes.string,
        content: PropTypes.string,
        refundPaymentMethods: PropTypes.array.isRequired,
        sizeGridToggle: PropTypes.bool,
        returnsCountry: PropTypes.string,
        isExchangable: PropTypes.bool,
        isPartialReturn: PropTypes.bool,
        guestUser: PropTypes.bool,
        returnTrackingUrl: PropTypes.string,
        orderStatus: PropTypes.string,
        courierTrackingId: PropTypes.string,
        links: PropTypes.array,
        isOrderCancelled: PropTypes.bool,
        isReturnRefused: PropTypes.bool,
        isOrderDetailsFetched: PropTypes.bool,
        returnCourierTrackingId: PropTypes.string,
        tracking: TrackingPropType,
        nativeApp: PropTypes.bool,
        isCoremediaV2: PropTypes.bool,
    }

    static defaultProps = {
        email: '',
        name: '',
    }

    setUserEipAndVipState(locale) {
        getUserSummary(locale, false).then((userSummary) => {
            const isVip = userSummary?.customerSegments?.customerCategory?.some((category) =>
                category.customerSegmentName.includes('VIP')
            )

            this.setState({ isEip: userSummary?.isEip, isVip })
        })
    }

    @track(
        myaccountPageData({
            name: 'order details',
            type: 'order details page',
            subCategory1: 'order details',
        })
    )
    componentDidMount() {
        const { externalOrderId, fetchOrderDetails, guestUser, locale, totals } = this.props

        this.setUserEipAndVipState(locale)

        if (guestUser) {
            const guestOrderEmail = window?.localStorage.getItem('guestOrderEmail')
            const guestOrderId = window?.localStorage.getItem('guestOrderId')

            if (guestOrderEmail && guestOrderId && externalOrderId.toLowerCase() === guestOrderId.toLowerCase()) {
                // If totals exists, the fetch was already done when submitting the form
                !totals && fetchOrderDetails(guestOrderId, true, guestOrderEmail)
            } else {
                window.location.assign(`/${locale.id}/account/track-order`)
                localStorage.removeItem('guestOrderEmail')
                localStorage.removeItem('guestOrderId')
            }
        } else {
            fetchOrderDetails(externalOrderId, false)
        }
    }

    componentDidUpdate(prevProps) {
        const { externalOrderId, fetchOrderDetails } = this.props
        if (prevProps.externalOrderId != externalOrderId) {
            fetchOrderDetails(externalOrderId, false)
        }
    }

    getReturnItems = (orderItems) => {
        return orderItems && orderItems.filter((item) => item?.isReturn)
    }

    render() {
        const {
            messages,
            className,
            config,
            orderDate,
            orderDateRaw,
            externalOrderId,
            displayId,
            totals,
            orderItems,
            orderItemTotal,
            shippingAddress,
            shippingMethod,
            paymentMethod,
            trackingUrl,
            shortDateShipped,
            dateShipped,
            dateShippedRaw,
            isReturnable,
            fetchOrderDetailsError,
            fetchOrderDetails,
            locale,
            returnOrder,
            createReturn,
            clearReturn,
            clearAllReturns,
            addReturn,
            push,
            selectReturnCode,
            selectReturnOrExchange,
            selectProductColour,
            returnSuccess,
            returnOrderError,
            email,
            name,
            userTitle,
            userLastName,
            returnItems,
            location,
            fetchContent,
            content,
            timeDelivered,
            timeDeliveredRaw,
            refundPaymentMethods,
            sizeGridFeatureToggle,
            returnsCountry,
            isLegacyOrder,
            isExchangable,
            guestUser,
            exchanges,
            returns,
            returnTrackingUrl,
            orderStatus,
            courierTrackingId,
            links,
            orderType,
            isOrderCancelled,
            activeOrderStatus,
            isReturnRefused,
            isPartialReturn,
            isOrderDetailsFetched,
            returnCourierTrackingId,
            nativeApp,
            customerEmailId,
            shipmentsWithItemDetails,
            returnLabelSuccess,
            returnLabelError,
            isFetchingReturnLabel,
            fetchReturnLabel,
            clearReturnLabelState,
            isCoremediaV2,
        } = this.props
        const urlPath = guestUser ? 'guest-order' : 'orders'

        const { isEip, isVip } = this.state
        const { collectionService = {}, flags, division } = config?.head
        const returnCourier = getCourierFromTrackingUrl(returnTrackingUrl)
        const disableBookACollectionForWeb =
            !nativeApp && flags?.disableWebReturnsPickupCountryList?.includes(shippingAddress?.country)
        const courier = getCourierFromCollectionService(
            returnCourier,
            collectionService,
            shippingAddress?.country,
            !(isEip || isVip)
        )

        const bookACollectionEnabled = !disableBookACollectionForWeb && courier?.bookACollectionEnabled
        const dropOffLocatorLink = courier?.dropOffLocatorLink || ''

        return (
            <div className={bem(null, guestUser && 'guest', className)}>
                {fetchOrderDetailsError ? (
                    <EmptyList
                        className={bem('error')}
                        variant={'myAccountError'}
                        title={messages.orderDetails.pageError()}
                    >
                        <Button
                            className={bem('retryButton')}
                            type="primary-link"
                            onClick={() => fetchOrderDetails(externalOrderId, guestUser)}
                        >
                            {messages.orderDetails.retryButton()}
                        </Button>
                    </EmptyList>
                ) : (
                    <>
                        <div className={bem('summary', guestUser && 'guest')}>
                            <Route
                                path={`/:locale/account/${urlPath}/:externalOrderId/return`}
                                render={({ match }) => {
                                    return (
                                        <div className={bem('timeline')}>
                                            <ExitWarning message={messages.orderDetails.exit()} />
                                            {division?.toUpperCase() === 'NAP' && guestUser ? (
                                                <h2 className={bem('title')}>
                                                    {messages.pages.orderdetailsReturn.title()}
                                                </h2>
                                            ) : null}
                                            <MultiStepForm
                                                location={location}
                                                match={match}
                                                horizontal={true}
                                                reverse={location.pathname.includes('confirmation')}
                                            >
                                                <Step
                                                    editable={false}
                                                    path={`/:locale/account/${urlPath}/:externalOrderId/return`}
                                                    messages={messages}
                                                    title={messages.orderDetails.createStep()}
                                                    completed={true}
                                                    exact={true}
                                                    small={true}
                                                    locale={locale}
                                                />
                                                <Step
                                                    editable={false}
                                                    path={`/:locale/account/${urlPath}/:externalOrderId/return/review`}
                                                    messages={messages}
                                                    title={messages.orderDetails.confirmStep()}
                                                    id={'2'}
                                                    completed={true}
                                                    exact={true}
                                                    small={true}
                                                    locale={locale}
                                                />
                                                {bookACollectionEnabled && (
                                                    <Step
                                                        editable={false}
                                                        path={`/:locale/account/${urlPath}/:externalOrderId/return/pickup`}
                                                        messages={messages}
                                                        title={messages.orderDetails.collectionStep()}
                                                        id={'3'}
                                                        completed={true}
                                                        exact={true}
                                                        small={true}
                                                        locale={locale}
                                                    />
                                                )}
                                                <Step
                                                    editable={false}
                                                    path={`/:locale/account/${urlPath}/:externalOrderId/return/confirmation`}
                                                    completedOnMatch={true}
                                                    messages={messages}
                                                    title={messages.orderDetails.completeStep()}
                                                    id={'4'}
                                                    completed={true}
                                                    exact={true}
                                                    small={true}
                                                    locale={locale}
                                                />
                                            </MultiStepForm>
                                        </div>
                                    )
                                }}
                            />
                            <Switch>
                                <Route path={`/:locale/account/${urlPath}/:externalOrderId/return/review`}>
                                    {returnSuccess ? (
                                        <Redirect
                                            to={`/${locale.id}/account/${urlPath}/${externalOrderId}/return/${
                                                bookACollectionEnabled
                                                    ? 'pickup'
                                                    : `confirmation${dropOffLocatorLink && '?id=drop-confirmation'}`
                                            }`}
                                        />
                                    ) : (
                                        <ScrollToTop key="reviewReturnForm">
                                            <ReviewReturn
                                                guestUser={guestUser}
                                                messages={messages}
                                                externalOrderId={externalOrderId}
                                                locale={locale}
                                                orderItems={orderItems}
                                                createReturn={createReturn}
                                                returnOrderError={returnOrderError}
                                                returnOrder={returnOrder}
                                                returnItems={returnItems}
                                                helpPage={config.head.links.returnsHelp}
                                                paymentMethod={paymentMethod}
                                                refundPaymentMethods={refundPaymentMethods}
                                                urlPath={urlPath}
                                                country={returnsCountry}
                                                email={customerEmailId}
                                                storeCreditExclusionList={flags?.storeCreditExclusionList}
                                                storeCreditReturnsExclusionList={flags?.storeCreditReturnsExclusionList}
                                                config={config}
                                            />
                                        </ScrollToTop>
                                    )}
                                </Route>
                                <Route path={`/:locale/account/${urlPath}/:externalOrderId/return/pickup`}>
                                    <ScrollToTop key="createCollection">
                                        <CreateCollection
                                            shippingAddress={shippingAddress}
                                            returnCourierTrackingId={returnCourierTrackingId}
                                            returnCourier={returnCourier}
                                            locale={locale}
                                            externalOrderId={externalOrderId}
                                            urlPath={urlPath}
                                            push={push}
                                            returnItems={returnItems}
                                            dropOffLocatorLink={dropOffLocatorLink}
                                            config={config}
                                        />
                                    </ScrollToTop>
                                </Route>
                                <Route path={`/:locale/account/${urlPath}/:externalOrderId/return/confirmation`}>
                                    <ScrollToTop>
                                        <ReturnConfirmation
                                            guestUser={guestUser}
                                            userTitle={userTitle}
                                            userLastName={userLastName}
                                            email={customerEmailId}
                                            fetchContent={fetchContent}
                                            content={content}
                                            messages={messages}
                                            returnItems={returnItems}
                                            locale={locale}
                                            returnCourier={returnCourier}
                                            dropOffLocatorLink={dropOffLocatorLink}
                                            returns={returns}
                                            externalOrderId={externalOrderId}
                                            fetchReturnLabel={fetchReturnLabel}
                                            clearReturnLabelState={clearReturnLabelState}
                                            returnLabelSuccess={returnLabelSuccess}
                                            returnLabelError={returnLabelError}
                                            isFetchingReturnLabel={isFetchingReturnLabel}
                                            shippingMethod={shippingMethod}
                                            isCoremediaV2={isCoremediaV2}
                                        />
                                    </ScrollToTop>
                                </Route>
                                <Route
                                    path={`/:locale/account/${urlPath}/:externalOrderId/return`}
                                    render={({ match }) => {
                                        return (
                                            <ScrollToTop key="createReturn">
                                                <CreateReturn
                                                    messages={messages}
                                                    match={match}
                                                    clearReturn={clearReturn}
                                                    addReturn={addReturn}
                                                    push={push}
                                                    selectReturnCode={selectReturnCode}
                                                    selectProductColour={selectProductColour}
                                                    externalOrderId={externalOrderId}
                                                    returnOrder={returnOrder}
                                                    orderItems={orderItems}
                                                    locale={locale}
                                                    selectReturnOrExchange={selectReturnOrExchange}
                                                    clearAllReturns={clearAllReturns}
                                                    returnItems={returnItems}
                                                    sizeGridFeatureToggle={sizeGridFeatureToggle}
                                                    isExchangable={isExchangable}
                                                    urlPath={urlPath}
                                                    email={email}
                                                />
                                            </ScrollToTop>
                                        )
                                    }}
                                />
                                <Route
                                    path={`/:locale/account/${urlPath}/:externalOrderId/track`}
                                    render={({ match: { params } }) => {
                                        return (
                                            <ScrollToTop key="orderTracking">
                                                <div className={bem('backButton')}>
                                                    <PreviousPageLink
                                                        links={links}
                                                        messages={messages}
                                                        location={location}
                                                        params={{ locale: locale.id, externalOrderId }}
                                                        title={
                                                            messages.orderTracking?.backTitle?.({ externalOrderId })
                                                                ? messages.orderTracking.backTitle({ externalOrderId })
                                                                : messages.pages?.orderdetails?.title?.()
                                                        }
                                                    />
                                                </div>
                                                <OrderTracking
                                                    externalOrderId={params.externalOrderId}
                                                    isCoremediaV2={isCoremediaV2}
                                                    {...this.props}
                                                />
                                            </ScrollToTop>
                                        )
                                    }}
                                />
                                <Route path={`/:locale/account/${urlPath}/:externalOrderId/pickup`}>
                                    {() => {
                                        if (bookACollectionEnabled && returnCourier) {
                                            return (
                                                <ScrollToTop key="createCollection">
                                                    <CreateCollection
                                                        shippingAddress={shippingAddress}
                                                        returnCourierTrackingId={returnCourierTrackingId}
                                                        returnCourier={returnCourier}
                                                        locale={locale}
                                                        externalOrderId={externalOrderId}
                                                        urlPath={urlPath}
                                                        push={push}
                                                        returnItems={this.getReturnItems(orderItems)}
                                                        dropOffLocatorLink={dropOffLocatorLink}
                                                        config={config}
                                                        trackingUrl={trackingUrl}
                                                        returnTrackingUrl={returnTrackingUrl}
                                                    />
                                                </ScrollToTop>
                                            )
                                        } else {
                                            return (
                                                <Redirect to={`/${locale.id}/account/${urlPath}/${externalOrderId}`} />
                                            )
                                        }
                                    }}
                                </Route>
                                <Route>
                                    <ScrollToTop key="orderInformation">
                                        <OrderInformation
                                            messages={messages}
                                            shipmentsWithItemDetails={shipmentsWithItemDetails}
                                            orderDate={orderDate}
                                            orderDateRaw={orderDateRaw}
                                            orderItemTotal={orderItemTotal}
                                            shippingAddress={shippingAddress}
                                            shippingMethod={shippingMethod}
                                            trackingUrl={trackingUrl}
                                            returnTrackingUrl={returnTrackingUrl}
                                            isReturnable={isReturnable}
                                            isLegacyOrder={isLegacyOrder}
                                            shortDateShipped={shortDateShipped}
                                            dateShipped={dateShipped}
                                            dateShippedRaw={dateShippedRaw}
                                            externalOrderId={externalOrderId}
                                            displayId={displayId}
                                            paymentMethod={paymentMethod}
                                            locale={locale}
                                            totals={totals}
                                            orderItems={orderItems}
                                            customerName={name}
                                            userTitle={userTitle}
                                            userLastName={userLastName}
                                            timeDelivered={timeDelivered}
                                            timeDeliveredRaw={timeDeliveredRaw}
                                            returnsCountry={returnsCountry}
                                            isExchangable={isExchangable}
                                            urlPath={urlPath}
                                            guestUser={guestUser}
                                            exchanges={exchanges}
                                            returns={returns}
                                            config={config}
                                            orderStatus={orderStatus}
                                            activeOrderStatus={activeOrderStatus}
                                            courierTrackingId={courierTrackingId}
                                            orderType={orderType}
                                            isOrderCancelled={isOrderCancelled}
                                            isReturnRefused={isReturnRefused}
                                            isPartialReturn={isPartialReturn}
                                            isOrderDetailsFetched={isOrderDetailsFetched}
                                            bookReturnEnabledInCountry={bookACollectionEnabled}
                                            fetchReturnLabel={fetchReturnLabel}
                                            clearReturnLabelState={clearReturnLabelState}
                                            returnLabelSuccess={returnLabelSuccess}
                                            returnLabelError={returnLabelError}
                                            isFetchingReturnLabel={isFetchingReturnLabel}
                                            email={customerEmailId}
                                            isEip={this.state.isEip}
                                        />
                                    </ScrollToTop>
                                </Route>
                            </Switch>
                        </div>
                    </>
                )}
            </div>
        )
    }
}
